"use client";

/**
 * The signed-in page: the harness's web app, booted into this element.
 *
 * The harness draws all of it — its frame, the sidebar with the conversation list and the scheduled-task page, the
 * conversation panel, the right sidebar and the Settings panel — with Orik in the seats it leaves to a deployment
 * (`harness/orik-harness.ts`). This component gives the harness its element and the facts it needs from the page:
 * who is signed in, whether voice input is configured, and what the URL asked for (a conversation, a Settings
 * section, the scheduled-task page). It keeps `?conversation=` naming the conversation on screen.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { mountDshClient } from "@/lib/agent/dsh-client-boot";
import { openConversation, useMainSession } from "@/lib/agent/navigation";
import { requestShellLink, settledUrl, type ShellLink } from "@/lib/agent/shell-links";
import { useAccount } from "@/lib/account";
import { applyStoredTheme } from "../../harness/orik-settings";
import { useTranscription } from "@/lib/transcription";
import "@/app/orik-dsh.css";

/**
 * Keep `?conversation=` naming the conversation on screen, so a reload or a shared link opens it again.
 *
 * Replaced rather than pushed: the conversation list is the navigation, and browser history would otherwise
 * fill with every row clicked.
 */
function useConversationUrl(): void {
  const main = useMainSession();
  useEffect(() => {
    if (main === undefined) return;
    const query = new URLSearchParams(window.location.search);
    if (query.get("conversation") === main) return;
    query.set("conversation", main);
    window.history.replaceState(null, "", `${window.location.pathname}?${query.toString()}${window.location.hash}`);
  }, [main]);
}

/** The frame's panel switch, as far as a page link uses it. */
interface Layout {
  selectPanel(id: string | null): void;
}

/** The slot registry, as far as waiting for a main panel needs it. */
interface Slots {
  entries(key: string): readonly { readonly options: { readonly key?: unknown } }[];
  subscribe(key: string, listener: () => void): () => void;
}

/** How long a page link waits for its panel to be registered before it gives up and leaves the conversation. */
const PANEL_WAIT_MS = 30_000;

/**
 * Show a main panel once its plugin has registered it.
 *
 * A plugin may register its panel after boot: since dsh 0.2.2 `ui-schedule` adds its page only once the host
 * grants this caller `schedule/catalog`, which arrives over the connection. So the link waits on the `main`
 * slot rather than selecting a panel the frame does not know yet, which it refuses.
 */
function selectPanelWhenRegistered(context: { get(name: string): unknown }, panelId: string): void {
  const layout = context.get("layout") as Layout | undefined;
  const slots = context.get("slots") as Slots | undefined;
  if (layout === undefined || slots === undefined) return;
  const registered = () => slots.entries("main").some(entry => entry.options.key === panelId);
  if (registered()) { layout.selectPanel(panelId); return; }
  const timer = setTimeout(() => {
    stop();
    console.warn("orik web: the linked panel was never registered", { panelId });
  }, PANEL_WAIT_MS);
  const stop = slots.subscribe("main", () => {
    if (!registered()) return;
    stop();
    clearTimeout(timer);
    layout.selectPanel(panelId);
  });
}

export function AssistantApp({ user, link, initialThreadId, transcriptionEnabled = false }: {
  user: string;
  link: ShellLink;
  initialThreadId?: string | undefined;
  transcriptionEnabled?: boolean;
}): ReactNode {
  const mount = useRef<HTMLDivElement>(null);
  useEffect(() => { useAccount.setState({ user }); }, [user]);
  useEffect(() => { useTranscription.setState({ enabled: transcriptionEnabled }); }, [transcriptionEnabled]);
  useConversationUrl();
  useEffect(() => {
    const element = mount.current;
    if (element === null) return;
    // Once per page: the harness takes this element for the rest of the page's life.
    applyStoredTheme();
    requestShellLink(link);
    // `/settings` and `/tasks` are opened once; a reload shows the conversation again.
    if (link.settingsSection !== undefined || link.panel !== undefined) window.history.replaceState(null, "", settledUrl(window.location));
    void mountDshClient(element).then(handle => {
      if (link.panel === undefined) return;
      selectPanelWhenRegistered(handle.context as { get(name: string): unknown }, link.panel);
    }).catch((reason: unknown) => {
      console.error("orik web: the harness client could not start", reason);
    });
    // The link is the page load's; a later change of props is not a new one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (initialThreadId === undefined) return;
    void openConversation(initialThreadId).catch((reason: unknown) => {
      console.warn("orik web: the linked conversation could not be opened", reason);
    });
  }, [initialThreadId]);
  return <div ref={mount} className="orik-dsh-app" />;
}
